'use client';

import React, { useEffect, useState } from "react";
import type { Locale } from '../../../../i18n-config';
import { Skeleton } from '@nextui-org/react';
import CarouselMiddle from "../carousel/carouselmiddle";

type Props = {
    lang: Locale
}

const ProgramSection = (props: Props) => {
    const { lang } = props;
    const [dictionary, setDictionary] = useState<any>(null);
    const [carousel, setCarousel] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch dictionary from API
                const dictResponse = await fetch(`/api/dictionary?lang=${lang}`);
                if (dictResponse.ok) {
                    const dict = await dictResponse.json();
                    setDictionary(dict);
                } else {
                    console.error('Failed to fetch dictionary');
                    setError('Failed to load dictionary');
                    setLoading(false);
                    return;
                }

                // Fetch carousel data
                const carouselResponse = await fetch(`/api/carousel/middle-main?lang=${lang}`);
                if (carouselResponse.ok) {
                    const carouselData = await carouselResponse.json();
                    setCarousel(carouselData);
                } else if (carouselResponse.status === 404) {
                    setError('Carousel not found');
                } else {
                    setError('Failed to load carousel');
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load content');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [lang]);

    if (loading) {
        return (
            <div className="my-6 md:my-16 lg:my-8">
                <div className="py-10 text-balance">
                    {/* Title skeleton */}
                    <Skeleton className="h-12 w-3/4 mx-auto mb-4 bg-gray-200 dark:bg-gray-700" />

                    {/* Subtitle skeleton */}
                    <Skeleton className="h-6 w-1/2 mx-auto bg-gray-200 dark:bg-gray-700" />
                </div>

                {/* Carousel skeleton - 3 column grid */}
                <div className="mx-auto grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                    {/* First card skeleton */}
                    <div className="relative overflow-hidden border-none shadow-large rounded-large" style={{ minHeight: '320px' }}>
                        {/* Background image skeleton */}
                        <Skeleton className="w-full h-full rounded-none" />

                        {/* Gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40" />

                        {/* Content skeleton */}
                        <div className="relative z-10 flex flex-col justify-end p-8 h-full">
                            {/* Header skeleton */}
                            <Skeleton className="h-6 w-3/4 mb-3 bg-white/20" />

                            {/* Subheader skeleton */}
                            <Skeleton className="h-4 w-full mb-2 bg-white/20" />
                            <Skeleton className="h-4 w-2/3 bg-white/20" />
                        </div>

                        {/* Footer skeleton */}
                        <div className="justify-between before:bg-white/10 border-white/20 border-0 overflow-hidden py-1 absolute before:rounded-xl rounded-large bottom-1 w-[calc(100%_-_8px)] shadow-small ml-1 z-10">
                            <Skeleton className="h-8 w-24 bg-white/20 rounded-lg" />
                        </div>
                    </div>

                    {/* Second card skeleton */}
                    <div className="relative overflow-hidden border-none shadow-large rounded-large" style={{ minHeight: '320px' }}>
                        <Skeleton className="w-full h-full rounded-none" />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40" />
                        <div className="relative z-10 flex flex-col justify-end p-8 h-full">
                            <Skeleton className="h-6 w-4/5 mb-3 bg-white/20" />
                            <Skeleton className="h-4 w-full mb-2 bg-white/20" />
                            <Skeleton className="h-4 w-3/4 bg-white/20" />
                        </div>
                        <div className="justify-between before:bg-white/10 border-white/20 border-0 overflow-hidden py-1 absolute before:rounded-xl rounded-large bottom-1 w-[calc(100%_-_8px)] shadow-small ml-1 z-10">
                            <Skeleton className="h-8 w-20 bg-white/20 rounded-lg" />
                        </div>
                    </div>

                    {/* Third card skeleton */}
                    <div className="relative overflow-hidden border-none shadow-large rounded-large" style={{ minHeight: '320px' }}>
                        <Skeleton className="w-full h-full rounded-none" />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40" />
                        <div className="relative z-10 flex flex-col justify-end p-8 h-full">
                            <Skeleton className="h-6 w-2/3 mb-3 bg-white/20" />
                            <Skeleton className="h-4 w-full mb-2 bg-white/20" />
                            <Skeleton className="h-4 w-1/2 bg-white/20" />
                        </div>
                        <div className="justify-between before:bg-white/10 border-white/20 border-0 overflow-hidden py-1 absolute before:rounded-xl rounded-large bottom-1 w-[calc(100%_-_8px)] shadow-small ml-1 z-10">
                            <Skeleton className="h-8 w-28 bg-white/20 rounded-lg" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-[70vh] bg-gray-100 dark:bg-gray-800">
                <div className="text-center">
                    <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                    <p className="text-gray-600 dark:text-gray-400">Please try refreshing the page</p>
                </div>
            </div>
        );
    }

    if (!carousel || !dictionary) {
        return (
            <div className="flex items-center justify-center h-[70vh] bg-gray-100 dark:bg-gray-800">
                <p className="text-gray-600 dark:text-gray-400">No carousel data available</p>
            </div>
        );
    }

    return (
      <div className="my-6 md:my-16 lg:my-8">
        <div className="py-10 text-balance text-center">
            <h2 className="text-4xl font-semibold tracking-tight text-gray-700 dark:text-gray-300 sm:text-5xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.18)] dark:drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
            {dictionary.homepage.programsection.title}
        </h2>
        <p className="mt-2 text-lg/8 text-gray-500">{dictionary.homepage.programsection.subtitle}</p>
        </div>
        
        <CarouselMiddle carousel={carousel} dictionary={dictionary} lang={lang} />
      </div>
    );
};

export default ProgramSection;